import React from "react";
import { Button, Spin } from "antd";
import Events from "components/events";
import Icons from "components/icons";
import { Localize } from "components/service";
import useCheckQrPosterExport from "components/events/hooks/use-check-qr-poster-export";
import {
    STAFF_CHECK_QR_STATUS,
    isStaffCheckQrLoading,
} from "../helpers/staff-check-qr-helper";


const StaffManagementCheckInQrField = ({ event, checkQr }) => {
    const eventID = event?.id;
    const qrUrl = checkQr ? checkQr.qrUrl : event?.getCheckQrCodeUrl;
    const status = checkQr?.status || (
        qrUrl ? STAFF_CHECK_QR_STATUS.READY : STAFF_CHECK_QR_STATUS.MISSING
    );
    const ready = status === STAFF_CHECK_QR_STATUS.READY;
    const missing = status === STAFF_CHECK_QR_STATUS.MISSING;
    const outdated = [
        STAFF_CHECK_QR_STATUS.OUTDATED,
        STAFF_CHECK_QR_STATUS.REGENERATING,
        STAFF_CHECK_QR_STATUS.REGENERATION_FAILED,
    ].includes(status);
    const loading = isStaffCheckQrLoading(status);
    const actionDisabled = loading || checkQr?.locationSaving || !checkQr?.canGenerate;

    const positionDate = checkQr.positionDate;
    const { exportLoading, exportPoster, positionUpdatedAtText } = useCheckQrPosterExport({
        eventID,
        qrUrl,
        eventTitle: event?.title,
        positionDate,
        disabled: !ready || checkQr?.locationSaving,
    });
    const downloadPng = () => exportPoster(
        "png",
        Events.Helpers.CheckQr.downloadCheckQrPng
    );
    const downloadPdf = () => exportPoster(
        "pdf",
        Events.Helpers.CheckQr.downloadCheckQrPdf
    );
    const printQr = () => exportPoster(
        "print",
        Events.Helpers.CheckQr.printCheckQr
    );

    const generateQr = () => checkQr?.generateQr();

    const renderQrState = () => {
        if (status === STAFF_CHECK_QR_STATUS.GENERATING)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state" role="status">
                    <Spin size="small" />
                    <span><Localize>STAFF_MANAGEMENT.QrState_Generating</Localize></span>
                </div>
            );
        }

        if (status === STAFF_CHECK_QR_STATUS.REGENERATING)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state" role="status">
                    <Spin size="small" />
                    <span><Localize>STAFF_MANAGEMENT.QrState_Regenerating</Localize></span>
                </div>
            );
        }

        if (status === STAFF_CHECK_QR_STATUS.GENERATION_FAILED)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state staff-management-check-in-qr__state--error">
                    <span role="alert"><Localize>STAFF_MANAGEMENT.QrState_GenerationFailed</Localize></span>
                    <Button type="primary" disabled={ actionDisabled } onClick={ generateQr }>
                        <Localize>STAFF_MANAGEMENT.Action_RetryQrGeneration</Localize>
                    </Button>
                </div>
            );
        }

        if (status === STAFF_CHECK_QR_STATUS.REGENERATION_FAILED)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state staff-management-check-in-qr__state--error">
                    <span role="alert"><Localize>STAFF_MANAGEMENT.QrState_RegenerationFailed</Localize></span>
                    <Button type="primary" disabled={ actionDisabled } onClick={ generateQr }>
                        <Localize>STAFF_MANAGEMENT.Action_RetryQrGeneration</Localize>
                    </Button>
                </div>
            );
        }

        if (status === STAFF_CHECK_QR_STATUS.OUTDATED)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state staff-management-check-in-qr__state--warning">
                    <span role="alert"><Localize>STAFF_MANAGEMENT.QrState_Outdated</Localize></span>
                    { !checkQr?.canGenerate &&
                        <span className="staff-management-check-in-qr__state-hint">
                            <Localize>STAFF_MANAGEMENT.QrState_SaveValidSetup</Localize>
                        </span>
                    }
                    <Button type="primary" disabled={ actionDisabled } onClick={ generateQr }>
                        <Localize>STAFF_MANAGEMENT.Action_RegenerateQr</Localize>
                    </Button>
                </div>
            );
        }

        if (status === STAFF_CHECK_QR_STATUS.MISSING)
        {
            return (
                <div className="d-flex flex-column align-items-start w-100 staff-management-check-in-qr__state staff-management-check-in-qr__state--missing">
                    <strong><Localize>STAFF_MANAGEMENT.QrState_NoQrTitle</Localize></strong>
                    <span><Localize>STAFF_MANAGEMENT.QrState_NoQrDescription</Localize></span>
                </div>
            );
        }

        return null;
    };

    return (
        <section className="staff-management-check-in-qr">
            <h2><Localize>STAFF_MANAGEMENT.Section_CheckInQr</Localize></h2>

            { ready && qrUrl ?
                <div className="staff-management-check-in-qr__available">
                    <div className="staff-management-check-in-qr__image">
                        <img src={ qrUrl } alt="QR Code" />
                    </div>

                    <div className="d-flex flex-column staff-management-check-in-qr__actions">
                        <Button
                            className="btn-icon-text"
                            disabled={ checkQr?.locationSaving || positionDate.loading || !!exportLoading }
                            loading={ exportLoading === "png" }
                            onClick={ downloadPng }>
                            <Icons.Download />
                            <Localize>CHECK_QR.Button_DownloadPng</Localize>
                        </Button>
                        <Button
                            className="btn-icon-text"
                            disabled={ checkQr?.locationSaving || positionDate.loading || !!exportLoading }
                            loading={ exportLoading === "pdf" }
                            onClick={ downloadPdf }>
                            <Icons.Download />
                            <Localize>CHECK_QR.Button_DownloadPdf</Localize>
                        </Button>
                        <Button
                            className="btn-icon-text staff-management-check-in-qr__print"
                            disabled={ checkQr?.locationSaving || positionDate.loading || !!exportLoading }
                            loading={ exportLoading === "print" }
                            onClick={ printQr }>
                            <Icons.StaffPrinter />
                            <Localize>CHECK_QR.Button_Print</Localize>
                        </Button>
                    </div>
                </div> :
                <div className={ `staff-management-check-in-qr__unavailable${ missing ? " staff-management-check-in-qr__unavailable--missing" : "" }${ outdated ? " staff-management-check-in-qr__unavailable--outdated" : "" }` }>
                    { qrUrl ?
                        <div className="staff-management-check-in-qr__image staff-management-check-in-qr__image--outdated">
                            <img src={ qrUrl } alt="" aria-hidden="true" />
                            <span><Localize>STAFF_MANAGEMENT.QrState_OutdatedLabel</Localize></span>
                        </div> :
                        <div className="staff-management-check-in-qr__empty">
                            <Icons.StaffNoQrCode aria-hidden="true" />
                        </div>
                    }

                    { renderQrState() }
                </div>
            }

            { qrUrl &&
                <div className="check-qr-position-date" aria-live="polite">
                    { positionDate.loading ? <Spin size="small" /> : positionUpdatedAtText }
                </div>
            }

            { qrUrl &&
                <p className={ loading ? "staff-management-check-in-qr__description--muted" : "" }>
                    <Localize>STAFF_MANAGEMENT.Text_QrDescription</Localize>
                </p>
            }
        </section>
    );
};

export default StaffManagementCheckInQrField;
